import React, { useEffect, useRef, useState } from 'react';
import { InventoryPanel } from '../mechanics/inventory/InventoryPanel';
import { WorkbenchHeader } from '../mechanics/workbench/WorkbenchHeader';
import { WorkbenchCanvas } from '../mechanics/workbench/WorkbenchCanvas';
import { InstructionsBanner } from '../mechanics/workbench/InstructionsBanner';
import { Cookbook } from '../components/Cookbook';
import { Shop } from '../components/Shop';
import { DragOverlaysPortal } from '../mechanics/dnd/DragOverlaysPortal';
import { AnimatePresence, motion } from 'framer-motion';
import { BookOpen, Calendar, ShoppingCart } from 'lucide-react';
import { useInventoryContext, useUIContext, useWorkbenchContext, useGameLoopContext, useShopContext, useGameStateContext } from './GameProviders';
import { ASSETS } from '../assetManifest.js';
import { OrderPanel } from '../components/OrderPanel';
import { PayoutToast } from '../components/PayoutToast';
import { ServingArea } from '../components/ServingArea';
import { PauseMenu } from '../components/PauseMenu';
import { SettingsMenu } from '../components/SettingsMenu';
import { TutorialOverlay } from '../components/TutorialOverlay';
import { DayTransitionNotification } from '../components/DayTransitionNotification';
import { DevConsole } from '../components/DevConsole';
import { SparkleEffect } from '../components/SparkleEffect';

export function AppShell() {
  const rootRef = useRef<HTMLDivElement>(null);

  const { inventory } = useInventoryContext();
  const { showCookbook, setShowCookbook, showShop, setShowShop, isPaused, setIsPaused } = useUIContext();
  const { combinationResult } = useWorkbenchContext();
  const { money, payoutToast, setPayoutToast, currentDay, currentWeek, isCustomerServingDay, hasCompletedFirstPlaythrough, showDayTransition, isRecipeCreationDay } = useGameLoopContext();
  const { shopState, purchaseItem, unlockPremiumCookbook } = useShopContext();
  const { setGameState } = useGameStateContext();

  const [shopButtonHovered, setShopButtonHovered] = React.useState(false);
  const [cookbookButtonHovered, setCookbookButtonHovered] = React.useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [showDevConsole, setShowDevConsole] = useState(false);

  // Show tutorial only on first day of first week if it's the first playthrough
  useEffect(() => {
    if (currentDay === 1 && currentWeek === 1 && !hasCompletedFirstPlaythrough) {
      setShowTutorial(true);
    }
  }, [currentDay, currentWeek, hasCompletedFirstPlaythrough]);

  // Request focus immediately so keyboard shortcuts work without prior clicks
  useEffect(() => {
    // Make the root focusable and focus it
    if (rootRef.current) {
      try {
        rootRef.current.focus();
      } catch {
        // ignore
      }
    }
    // Also try focusing the window (may be ignored by some browsers)
    try {
      window.focus();
    } catch {
      // ignore
    }

    const onWindowFocus = () => {
      // Re-focus root to keep keyboard handling consistent
      if (rootRef.current) {
        try {
          rootRef.current.focus();
        } catch {
          // ignore
        }
      }
    };
    window.addEventListener('focus', onWindowFocus);
    return () => window.removeEventListener('focus', onWindowFocus);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Support both "Escape" and legacy "Esc"
      if (e.key === 'Escape' || e.key === 'Esc') {
        // Close any open modals first, then toggle pause menu
        if (showCookbook) {
          setShowCookbook(false);
        } else if (showShop) {
          setShowShop(false);
        } else if (showSettings) {
          setShowSettings(false);
        } else if (showTutorial) {
          setShowTutorial(false);
        } else {
          // No modals open, toggle pause menu
          setIsPaused(prev => !prev);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [setIsPaused, showCookbook, showShop, showSettings, showTutorial, setShowCookbook, setShowShop, setShowSettings, setShowTutorial]);

  // Dev console toggle
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === '`') {
          setShowDevConsole(prev => !prev);
        }
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, []);

  // One-time asset validation in development
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      const bgCfg = (ASSETS as any)?.ui?.background;
      if (!bgCfg || !bgCfg.path || typeof bgCfg.path !== 'string') {
        throw new Error('ASSETS.ui.background.path is missing or invalid. Define a valid relative path in src/assetManifest.js');
      }
      if (bgCfg.path.startsWith('/')) {
        throw new Error("ASSETS.ui.background.path must be relative and must not start with '/'. Use e.g. 'assets/backgrounds/workbench-placeholder.webp'");
      }
      
      const settingsButtonCfg = (ASSETS as any)?.ui?.settings_button;
      const settingsButtonHoverCfg = (ASSETS as any)?.ui?.settings_button_hover;
      if (!settingsButtonCfg || !settingsButtonCfg.path || !settingsButtonHoverCfg || !settingsButtonHoverCfg.path) {
        throw new Error('Settings button assets are missing or invalid in assetManifest.js');
      }
    }
  }, []);

  const backgroundImage: string = (ASSETS as any).ui.background.path;
  const settingsButtonCfg = (ASSETS as any).ui.settings_button;
  const settingsButtonHoverCfg = (ASSETS as any).ui.settings_button_hover;

  return (
    <div
      ref={rootRef}
      tabIndex={-1}
      className="app-root min-h-screen text-white font-sans overflow-hidden relative outline-none"
      style={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat',
        // Base color under the image
        backgroundColor: '#0f172a'
      }}
    >
      {/* Small title in top-left corner */}
      <div className="absolute top-4 left-4 z-50">
        <h1 className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
          Delichemy
        </h1>
      </div>

      {/* Top-right corner controls */}
      <div className="absolute top-4 right-4 z-50 flex items-center gap-4">
        {/* Day and Week Info */}
        <div className="flex items-center gap-2">
          <div className="inline-flex items-center gap-2 bg-slate-800/90 backdrop-blur rounded-full px-3 py-1 border border-blue-500/30">
            <Calendar className="w-4 h-4 text-blue-400" />
            <span className="text-blue-300 text-xs font-semibold">Week {currentWeek}</span>
            <span className="text-blue-200 text-xs font-bold">Day {currentDay}</span>
          </div>
        </div>

        <div className="flex items-center gap-2 bg-slate-800/90 backdrop-blur rounded-full px-4 py-2 border border-amber-500/30">
          <span className="text-amber-400 font-bold text-lg">${money}</span>
        </div>
        <button
          onClick={() => setShowShop(true)}
          onMouseEnter={() => setShopButtonHovered(true)}
          onMouseLeave={() => setShopButtonHovered(false)}
          className="relative flex items-center justify-center transition-transform transform hover:scale-105"
          style={{
            width: `${settingsButtonCfg.width}px`,
            height: `${settingsButtonCfg.height}px`,
            backgroundImage: `url(${shopButtonHovered ? settingsButtonHoverCfg.path : settingsButtonCfg.path})`,
            backgroundSize: '100% 100%',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
          }}
        >
          <span className="text-black font-medium text-lg drop-shadow flex items-center gap-2">
            <ShoppingCart className="w-5 h-5" />
            Shop
          </span>
        </button>
        <button
          onClick={() => setShowCookbook(true)}
          onMouseEnter={() => setCookbookButtonHovered(true)}
          onMouseLeave={() => setCookbookButtonHovered(false)}
          className="relative flex items-center justify-center transition-transform transform hover:scale-105"
          style={{
            width: `${settingsButtonCfg.width}px`,
            height: `${settingsButtonCfg.height}px`,
            backgroundImage: `url(${cookbookButtonHovered ? settingsButtonHoverCfg.path : settingsButtonCfg.path})`,
            backgroundSize: '100% 100%',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
          }}
        >
          <span className="text-black font-medium text-lg drop-shadow flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            Cookbook
          </span>
          {shopState.premiumCookbookUnlocked && (
            <span className="absolute -top-1 -right-1 bg-purple-500 text-xs px-1.5 py-0.5 rounded-full">Premium</span>
          )}
        </button>
      </div>

      {/* Inventory Panel - positioned to use more space */}
      <InventoryPanel />

      {/* Compact Workbench Area */}
      <div className="absolute left-[400px] right-[400px] top-16 bottom-4">
        <div className="h-full flex flex-col">
          <WorkbenchHeader />
          <WorkbenchCanvas />
          <InstructionsBanner />
        </div>
      </div>

      {/* Combination Result (toast) */}
      <AnimatePresence>
        {combinationResult && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className={`fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 text-center p-4 rounded-lg overflow-hidden ${
              combinationResult.success
                ? 'bg-green-500/20 border border-green-500/50'
                : 'bg-red-500/20 border border-red-500/50'
            }`}
          >
            {combinationResult.success && combinationResult.result && <SparkleEffect />}
            <p className="font-semibold relative z-10">{combinationResult.message}</p>
            {combinationResult.result && (
              <div className="mt-2 text-3xl font-bold flex items-center justify-center gap-3 relative z-10">
                <span>{/* icon and name pulled from data.js within hook originally; leave as message only */}</span>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Drag overlays via portal */}
      <DragOverlaysPortal />

      {/* Order Panel - only show on customer serving days */}
      {isCustomerServingDay && <OrderPanel />}

      {/* Serving Area - only show on customer serving days */}
      {isCustomerServingDay && <ServingArea />}

      {/* Shop */}
      <Shop
        show={showShop}
        onClose={() => setShowShop(false)}
        money={money}
        currentDay={currentDay}
        premiumCookbookUnlocked={shopState.premiumCookbookUnlocked}
        onPurchaseItem={purchaseItem}
        onUnlockPremiumCookbook={unlockPremiumCookbook}
        ownedItems={shopState.ownedItems}
      />

      {/* Cookbook */}
      <Cookbook
        show={showCookbook}
        onClose={() => setShowCookbook(false)}
        inventory={inventory}
        premiumUnlocked={shopState.premiumCookbookUnlocked}
      />

      {/* Payout Toast */}
      <PayoutToast payout={payoutToast} onClose={() => setPayoutToast(null)} />

      {/* Pause Menu */}
      <PauseMenu
        show={isPaused}
        onResume={() => setIsPaused(false)}
        onOpenSettings={() => {
          setIsPaused(false);
          setShowSettings(true);
        }}
        onGoToMainMenu={() => {
          setIsPaused(false);
          setGameState('title');
        }}
      />

      {/* Settings Menu */}
      <SettingsMenu 
        show={showSettings} 
        onClose={() => setShowSettings(false)} 
      />

      {/* Tutorial Overlay */}
      <TutorialOverlay
        show={showTutorial}
        onClose={() => setShowTutorial(false)}
      />

      {/* Day Transition Notification */}
      <DayTransitionNotification
        show={showDayTransition}
        currentDay={currentDay}
        currentWeek={currentWeek}
        isRecipeCreationDay={isRecipeCreationDay}
        isCustomerServingDay={isCustomerServingDay}
      />

      {/* Dev Console */}
      {process.env.NODE_ENV !== 'production' && showDevConsole && <DevConsole />}
    </div>
  );
}
